import { ScanBarcode } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Field, Input, Select } from '@/components/ui';
import {
  type DispensingRx,
  type ScanBody,
  type ScanResult,
  useSecondCheckers,
} from '@/features/admin-v2/api';
import { isApiError } from '@/lib/api';
import { itemForCode } from './safety-checks';
import styles from './DispensaryPage.module.css';

type Outcome = { tone: 'success' | 'warning' | 'danger'; text: string };

/**
 * RL-1629: the box scans of one prescription, recorded by the API. The
 * barcode scanner types the code and Enter into one focused input; the code
 * picks its item (`<medicine code>|<batch>`) or goes to the chosen item. A
 * manual scan confirmation types the batch number from the box with a reason
 * and is checked by the API exactly like a scan. Nothing is ticked here
 * without the API's answer.
 */
export function ScanPanel({
  rx,
  scanning,
  onScan,
}: {
  rx: DispensingRx;
  scanning: boolean;
  onScan: (itemId: string, body: ScanBody) => Promise<ScanResult>;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'scanner' | 'manual'>('scanner');
  const firstOpen = rx.items.find((i) => !i.scanned)?.id ?? rx.items[0]?.id;
  const [itemId, setItemId] = useState(firstOpen ?? '');
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  // RL-1629 review: a controlled, psychotropic or high-alert box confirmed by
  // hand needs a second person's own PIN.
  const [attester, setAttester] = useState('');
  const [attesterPin, setAttesterPin] = useState('');
  const chosen = rx.items.find((i) => i.id === itemId);
  const needsAttestation =
    mode === 'manual' &&
    !!(
      chosen?.catalogue?.controlled ||
      chosen?.catalogue?.psychotropic ||
      chosen?.catalogue?.highAlert
    );
  const attesters = useSecondCheckers(rx, needsAttestation);
  const attestationReady =
    !needsAttestation || (attester !== '' && /^\d{4,8}$/.test(attesterPin));
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const scanInput = useRef<HTMLInputElement>(null);
  const done = rx.items.filter((i) => i.scanned).length;

  // The scanner types into this input: focus it once the dialog has set
  // its own initial focus.
  useEffect(() => {
    if (mode !== 'scanner') return;
    const timer = window.setTimeout(() => scanInput.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [mode]);

  const send = (target: string, body: ScanBody) => {
    const item = rx.items.find((i) => i.id === target);
    setOutcome(null);
    void onScan(target, body)
      .then((updated) => {
        setCode('');
        setReason('');
        setAttester('');
        setAttesterPin('');
        const next = updated.items.find((i) => !i.scanned);
        if (next) setItemId(next.id);
        setOutcome(
          updated.warning
            ? { tone: 'warning', text: updated.warning }
            : {
                tone: 'success',
                text: t('dispensary.scanOk', { medicine: item?.medicine }),
              },
        );
        scanInput.current?.focus();
      })
      .catch((err: unknown) => {
        setOutcome({
          tone: 'danger',
          text: isApiError(err) ? err.message : t('dispensary.scanFailed'),
        });
        scanInput.current?.select();
      });
  };

  const submitScan = (event: FormEvent) => {
    event.preventDefault();
    const value = code.trim();
    if (!value) return;
    const target = itemForCode(rx, value) ?? itemId;
    if (target) send(target, { code: value });
  };

  const submitManual = (event: FormEvent) => {
    event.preventDefault();
    if (!itemId || !code.trim() || reason.trim().length < 3) return;
    if (!attestationReady) return;
    send(itemId, {
      code: code.trim(),
      manualReason: reason.trim(),
      ...(needsAttestation
        ? { attestation: { staffId: attester, pin: attesterPin } }
        : {}),
    });
  };

  const itemOptions = rx.items.map((i) => ({
    value: i.id,
    label: i.scanned
      ? t('dispensary.itemScannedOption', { medicine: i.medicine })
      : i.medicine,
  }));

  return (
    <fieldset className={styles.safetyPanel}>
      <legend>{t('dispensary.scanMethod')}</legend>
      <div
        className={styles.choiceRow}
        role="group"
        aria-label={t('dispensary.scanMethod')}
      >
        <Button
          variant={mode === 'scanner' ? 'primary' : 'secondary'}
          aria-pressed={mode === 'scanner'}
          icon={<ScanBarcode size={16} />}
          onClick={() => setMode('scanner')}
        >
          {t('dispensary.scanMedicines')}
        </Button>
        <Button
          variant={mode === 'manual' ? 'primary' : 'secondary'}
          aria-pressed={mode === 'manual'}
          onClick={() => setMode('manual')}
        >
          {t('dispensary.manualScan')}
        </Button>
      </div>
      <p className={styles.sub} aria-live="polite">
        {t('dispensary.scanProgress', { done, total: rx.items.length })}
      </p>
      {done < rx.items.length &&
        (mode === 'scanner' ? (
          <form className={styles.scanForm} onSubmit={submitScan}>
            <Field label={t('dispensary.scanItem')}>
              <Select
                value={itemId}
                options={itemOptions}
                onChange={(event) => setItemId(event.target.value)}
              />
            </Field>
            <Field
              label={t('dispensary.scanCode')}
              hint={t('dispensary.scanCodeHint')}
            >
              <Input
                ref={scanInput}
                value={code}
                autoComplete="off"
                maxLength={80}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
            <Button type="submit" loading={scanning} disabled={!code.trim()}>
              {t('dispensary.scanRecord')}
            </Button>
          </form>
        ) : (
          <form className={styles.scanForm} onSubmit={submitManual}>
            <p className={styles.sub}>{t('dispensary.scanHelp')}</p>
            <Field label={t('dispensary.scanItem')}>
              <Select
                value={itemId}
                options={itemOptions}
                onChange={(event) => setItemId(event.target.value)}
              />
            </Field>
            <Field label={t('dispensary.manualBatch')}>
              <Input
                value={code}
                autoComplete="off"
                maxLength={80}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
            <Field
              label={t('dispensary.manualReason')}
              hint={t('dispensary.manualReasonHint')}
            >
              <Input
                value={reason}
                maxLength={200}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            {needsAttestation && (
              <>
                <Field
                  label={t('dispensary.attester')}
                  hint={t('dispensary.attesterHint')}
                >
                  <Select
                    value={attester}
                    placeholder={t('dispensary.chooseChecker')}
                    options={(attesters.data ?? []).map((c) => ({
                      value: c.id,
                      label: c.fullName,
                    }))}
                    onChange={(event) => {
                      setAttester(event.target.value);
                      setAttesterPin('');
                    }}
                  />
                </Field>
                {attester !== '' && (
                  <Field label={t('dispensary.attesterPin')}>
                    <Input
                      type="password"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={8}
                      value={attesterPin}
                      onChange={(event) =>
                        setAttesterPin(event.target.value.replace(/\D/g, ''))
                      }
                    />
                  </Field>
                )}
              </>
            )}
            <Button
              type="submit"
              loading={scanning}
              disabled={
                !code.trim() || reason.trim().length < 3 || !attestationReady
              }
            >
              {t('dispensary.manualRecord')}
            </Button>
          </form>
        ))}
      {outcome && (
        <p
          className={
            outcome.tone === 'danger' ? styles.refusal : styles.scanOutcome
          }
          role={outcome.tone === 'danger' ? 'alert' : 'status'}
        >
          {outcome.text}
        </p>
      )}
      <ul className={styles.scanList}>
        {rx.items.map((item) => (
          <li key={item.id} className={styles.scanItem}>
            <span>{item.medicine}</span>
            <Badge tone={item.scanned ? 'success' : 'warning'}>
              {item.scanned
                ? item.scanned.manual
                  ? t('dispensary.scannedManual', {
                      batch: item.scanned.batchNo ?? '',
                    })
                  : t('dispensary.scannedBatch', {
                      batch: item.scanned.batchNo ?? '',
                    })
                : t('dispensary.awaitingScan')}
            </Badge>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}
